"use client";
import { ArrowDown, ArrowRight } from "@/components/icons/arrow";
import { Image } from "@/components/shared/image";
import { Button, buttonVariants } from "@/components/ui/button";
import { useTranslation } from "@/i18n/client";
import { Lang } from "@/i18n/settings";
import { cn } from "@/lib/utils";
import { getNews, NewsProps } from "@/services/news/news.server";
import { MetaProps } from "@/types";
import dayjs from "dayjs";

import Link from "@/components/shared/link";
import "dayjs/locale/ar";
import "dayjs/locale/en";
import localeData from "dayjs/plugin/localeData";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import GradientBorder from "@/components/common/gradient-border";

dayjs.extend(localeData);
dayjs.extend(LocalizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

type Props = {
  lang: Lang;
  data: NewsProps[];
  meta: MetaProps["pagination"];
};

export function PressReleases({ data: initialData, lang, meta }: Props) {
  dayjs.locale(lang);
  const { t } = useTranslation(lang, "news");

  const [hasMore, setHasMore] = useState((meta?.pageCount ?? 1) > 1);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<NewsProps[]>(initialData);
  const [loading, setLoading] = useState(false);

  async function loadMore() {
    if (!hasMore || loading) return;

    setLoading(true);

    const res = await getNews({
      page: page + 1,
      locale: lang,
      category: "PR",
      pageSize: 3,
    });

    if (res?.data) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      setData([...data, ...res?.data]);

      setHasMore(res.meta.pageCount > page + 1);
      setPage(page + 1);
    }

    setLoading(false);
  }

  return (
    <div className="flex flex-col gap-8">
      <h1 className="text-3xl text-secondary md:flex-[0.3] xl:flex-[0.6] xl:text-4xl ltr:font-playfair">
        {t("pressRelease")}
      </h1>

      <div className="flex flex-col gap-14">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((item) => (
            <div
              key={item.id}
              className="group relative h-[400px] w-full overflow-hidden rounded-[40px] bg-foreground md:h-[600px]"
            >
              <Image
                src={item?.image_link}
                alt={item.title}
                layout="fill"
                objectFit="cover"
                className="transition-transform duration-300 group-hover:scale-110"
              />

              <div className="absolute inset-0 z-30 hidden items-center justify-center group-hover:flex">
                <Link
                  href={`/news/${item.id}?title=` + item?.title}
                  className={buttonVariants({
                    className:
                      "!h-16 !w-16 !rounded-full !bg-transparent transition-all duration-200 ease-in hover:border-2",
                    size: "icon",
                    variant: "outline",
                  })}
                >
                  <ArrowRight
                    className="-rotate-45 rtl:-rotate-[135deg]"
                    width={"25"}
                    height={"26"}
                  />
                </Link>
              </div>

              <div
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.9) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%)",
                }}
                className="absolute inset-0 flex flex-col justify-between rounded-[40px] p-4 md:pt-6"
              >
                <div className="relative flex items-end gap-2 text-sm text-secondary sm:text-base">
                  <p className="">{t("pressReleaseTitle")}</p>
                  <p className="text-xl">.</p>

                  <p>{dayjs.utc(item?.timeline).tz("Asia/Riyadh").format("ll")}</p>
                </div>

                <div className="flex flex-col gap-2 text-secondary">
                  <p className="line-clamp-1 text-xl sm:text-2xl md:line-clamp-2">{item.title}</p>
                  <p className="line-clamp-2 text-sm text-secondary/60 sm:text-base md:line-clamp-2 ltr:font-opensans">
                    {item.sub_title}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {loading && (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="relative flex h-[400px] w-full flex-col justify-between overflow-hidden rounded-[40px] bg-foreground py-4 pt-10 md:h-[600px]"
              >
                <div className="flex gap-4 px-6">
                  <Skeleton className="h-4 w-8" />
                  <Skeleton className="h-4 w-20" />
                </div>

                <div className="sm:px- gap-4 p-4 sm:gap-6 sm:pt-20">
                  <div>
                    <div className="flex flex-col gap-2">
                      <Skeleton className="h-4 w-[90%]" />
                      <Skeleton className="h-4 w-[60%]" />
                      <Skeleton className="h-3 w-32" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className={cn("flex items-center justify-center", !hasMore && "hidden")}>
          <Button
            onClick={loadMore}
            disabled={loading || !hasMore}
            variant="outline"
            className="0 relative flex h-12 max-w-fit gap-2 rounded-full border-none bg-foreground px-8 font-normal uppercase text-secondary/70 opacity-80 backdrop-blur-md transition-all duration-150 ease-in hover:border-secondary hover:text-secondary/90 lg:h-14 xl:text-base ltr:font-sans"
          >
            <GradientBorder />
            {t("loadMore")}
            <ArrowDown />
          </Button>
        </div>
      </div>
    </div>
  );
}
